import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, Legend, CartesianGrid} from "recharts";

const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#FFFFFF", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#FFFFFF", "#FFFFFF"],
  DET: ["#0C2C56", "#FFFFFF", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#FFFFFF", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CD6", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#FEC430", "#FFFFFF"],
  STL: ["#BE0A15", "#FDDB00", "#FFFFFF"],
  LAD: ["#005A9D", "#FFFFFF", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
  "0": ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};


export default function RollingGraphs(props: any) {
  const [rollingData, setRollingData] = useState<any[]>([]);
  const [stats, setStats] = useState<string[]>(['wOBA']);
  const [games, setGames] = useState<string>('10');

  const statOptions = ['wOBA', 'xwOBA', 'OBP', 'xOBP', 'BA', 'xBA', 'SLG', 'xSLG', 'BB%', 'K%', "HardHit%", "Barrel%", 'BABIP', "Swing%", "Whiff%"];
  const rollingKeys: { [key: string]: string } = {
    'wOBA': 'rolling_woba',
    'xwOBA': 'rolling_xwoba',
    'OBP': 'rolling_obp',
    'xOBP': 'rolling_xobp',
    'BA': 'rolling_ba',
    'xBA': 'rolling_xba',
    'SLG': 'rolling_slg',
    'xSLG': 'rolling_xslg',
    'BB%': 'rolling_bb_percent',
    'K%': 'rolling_k_percent',
    "HardHit%": "rolling_hardhit_percent",
    "Barrel%": "rolling_barrels_per_bbe_percent",
    "BABIP": "rolling_babip",
    "Swing%": "rolling_swing_percent",
    "Whiff%": "rolling_whiff_percent"
  };

  useEffect(() => {
    fetch(props.prod ? "https://oriolebird.pythonanywhere.com/rolling-data/" + props.id + '/' + props.season + '/' + (games === '' ? '1' : games) + '/' + props.type : "/rolling-data/" + props.id + '/' + props.season + '/' + (games === '' ? '1' : games) + '/' + props.type)
      .then((res) => res.json())
      .then((pData) => {
        const pgData = pData.map((d: any, i: number) => ({
          ...d,
          game: i + 1,
        }))
        //console.log("Rolling Data for ", props.id, props.season, games, props.type, pData);
        setRollingData(pgData)
      });
  }, [props.id, props.prod, props.season, games, props.type]);

  const temp = Object.keys(props).includes('teamInfoProp')
  if (temp) {
    props.playerInfoProp.common = { teamInfo: props.teamInfoProp }
  }

  const w = props.isDesktop ? 500 : 350

  const lineColors = ["#D22D49", "#33DAF1", "#FE9D00", "#1DBE3A", "#94412E", "#62BDBD", "#7B36D7", "#93AFD4"];

  const getGameTickStep = (games: number) => {
    if (games >= 80) return 20;
    if (games >= 40) return 10;
    if (games >= 20) return 5;
    return 2;
  };

  const generateGameTicks = (games: number) => {
    const step = getGameTickStep(games);
    const ticks: number[] = [];

    for (let i = step; i <= games; i += step) {
      ticks.push(i);
    }

    return ticks;
  };

  const handleChange = (event: SelectChangeEvent<typeof stats>) => {
    const {
      target: { value },
    } = event;
    setStats(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  return (
    <div style={{ height: "100%" }}>
      <Grid
        container
        justifyContent="center"
        spacing={2}
        sx={{ height: "100%" }}
      >
        <Grid maxWidth="90vw" sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              maxWidth: "90vw",
              width: "500px",
              border: "#293241 solid 2px",
              backgroundColor: "#FFFFFF",
              height: "100%",
              minHeight: props.isDesktop ? "570px" : "auto",
              flexGrow: 1,
              boxShadow:
                "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
            }}
            justifyContent="flex-start"
            alignItems="center"
            display="flex"
            flexDirection="column"
          >
            <div
              style={{
                width: "100%",
                paddingTop: "10px",
                paddingBottom: "10px",
                backgroundColor:
                  props.playerInfoProp.common.teamInfo.MLB_AbbName !== null
                    ? teamColors[props.playerInfoProp.common.teamInfo.MLB_AbbName][0]
                    : teamColors["FA"][0],
              }}
            >
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
              >
                <Typography variant="h5">
                  <span
                    style={{
                      color:
                        props.playerInfoProp.common.teamInfo.MLB_AbbName !== null
                          ? teamColors[
                          props.playerInfoProp.common.teamInfo.MLB_AbbName
                          ][1]
                          : teamColors["FA"][1],
                      fontWeight: "bold",
                      fontSize: "25pt",
                      textAlign: "center"
                    }}
                  >
                    Rolling Graphs
                  </span>
                </Typography>
              </Grid>
            </div>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', alignItems: 'flex-start' }}>
              <FormControl sx={{ m: 1, width: 250 }}>
                <InputLabel >Stats</InputLabel>
                <Select
                  multiple
                  value={stats}
                  onChange={handleChange}
                  input={<OutlinedInput label="Stats" />}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {statOptions.map((stat) => (
                    <MenuItem key={stat} value={stat}>
                      <Checkbox checked={stats.includes(stat)} />
                      <ListItemText primary={stat} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ marginTop: 1, width: 70 }}>
                <TextField 
                  id="outlined-basic" 
                  label="Games" 
                  variant="outlined" 
                  type="number"
                  value={games} 
                  onChange={(e) => setGames(e.target.value)}
                  inputProps={{ min: 1, max: 162 }}
                />
              </FormControl>
            </Box>

            <ResponsiveContainer width={w} height={w * 0.85} style={{ marginTop: 0 }}>
              <LineChart
                data={rollingData}
                margin={{ top: 10, right: stats.some(stat => stat.includes('%')) ? 0 : 20, left: 0, bottom: 5 }}
              >
                {/* Background grid with subtle styling */}
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

                {/* X and Y axes with customized ticks and labels */}
                <XAxis
                  dataKey="game"
                  tick={{ fontSize: 12, fill: '#555' }}
                  domain={[1, rollingData.length]}
                  ticks={generateGameTicks(rollingData.length)}
                  label={{ value: 'Games', position: 'bottom', offset: -10 }}
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  domain={([dataMin, dataMax]) => {
                    const step = 0.01;

                    // Safety: dataMin/dataMax can be equal or undefined briefly
                    if (
                      !Number.isFinite(dataMin) ||
                      !Number.isFinite(dataMax) ||
                      dataMin === dataMax
                    ) {
                      const v = Number.isFinite(dataMin) ? dataMin : 0;
                      return [v - step, v + step];
                    }

                    // Safety: width may be 0 / undefined on first render
                    const ticks = Math.max(2, Math.ceil((w || 0) / 50));

                    const min = Math.floor(dataMin / step) * step;
                    const max = Math.ceil(dataMax / step) * step;

                    const range = max - min;
                    const target = step * (ticks - 1);

                    const snappedRange =
                      target > 0
                        ? Math.ceil(range / target) * target
                        : range;

                    return [min, min + snappedRange];
                  }}
                  tickCount={Math.max(2, Math.ceil((w || 0) / 50))}
                  allowDecimals
                  tick={{ fontSize: 12, fill: '#555' }}
                  tickFormatter={(v) => v.toFixed(3)}
                />
                {stats.some(stat => stat.includes('%')) && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={([dataMin, dataMax]) => {
                      const step = 5;

                      // Safety: dataMin/dataMax can be equal or undefined briefly
                      if (
                        !Number.isFinite(dataMin) ||
                        !Number.isFinite(dataMax) ||
                        dataMin === dataMax
                      ) {
                        const v = Number.isFinite(dataMin) ? dataMin : 0;
                        return [v - step, v + step];
                      }

                      // Safety: width may be 0 / undefined on first render
                      const ticks = 4;

                      const min = Math.floor(dataMin / step) * step;
                      const max = Math.ceil(dataMax / step) * step;

                      const range = max - min;
                      const target = step * (ticks - 1);

                      const snappedRange =
                        target > 0
                          ? Math.ceil(range / target) * target
                          : range;

                      return [min, min + snappedRange];
                    }}
                    tickCount={4}
                    allowDecimals
                    tick={{ fontSize: 12, fill: '#555' }}
                    tickFormatter={(v) => v.toFixed(0) + "%"}
                  />
                )}


                {/* Interactive tooltip */}
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', borderRadius: 5, border: '1px solid #ccc' }}
                  itemStyle={{ color: '#000' }}
                />

                {/* Legend with positioning */}
                <Legend
                  verticalAlign="top"
                  height={50}
                  width={w - 100}
                  iconSize={4}
                  iconType="circle"
                  wrapperStyle={{ paddingLeft: 50, }}
                />

                {/* Line with smooth curve, dots, and active dot */}
                {stats.map((s, i) =>
                  <Line
                    yAxisId={s.includes("%") ? "right" : "left"}
                    type="monotone"
                    dataKey={rollingKeys[s]}
                    stroke={lineColors[i]}
                    strokeWidth={2}
                    dot={{ r: 1 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />
                )}

                {/* Optional: Reference line for league average or threshold */}
                {/* <ReferenceLine y={0.320} stroke="red" strokeDasharray="3 3" label="League Avg" /> */}
              </LineChart>
            </ResponsiveContainer>

          </Box>
        </Grid>
      </Grid>
    </div>
  );
}